import { ModuleField } from '../../types/custom-fields/module-field'
import { translations } from '../translations/translations-manager'
import validation_schema from './user-validation-schema.json'
import { TranslatedTexts } from '../translations/translations-types'

/**
 * Generate a validation schema for a module based on its fields.
 * @param fields Module fields
 * @returns Ajv validation schema
 */
export const moduleValidationSchema = (fields: ModuleField[]): Record<string, any> => {
  const validationSchema = validation_schema as Record<string, any>
  const schema: Record<string, any> = {
    type: 'object',
    properties: {} satisfies Record<string, any>,
    required: [],
    additionalProperties: true,
  }

  fields.forEach(field => {
    const propertySchema = getTranslatedAjvPropertySchema(validationSchema[field.type] ?? validationSchema['text'])
    if (propertySchema === undefined) {
      throw new Error(`Unsupported FieldType: ${field.type as string}`)
    }

    if (field.required) {
      schema.required.push(field.name)
      if (propertySchema.type === 'string') {
        propertySchema.minLength = 1
      }
      if (propertySchema.type === 'boolean') {
        propertySchema.const = true
      }
    } else {
      propertySchema.nullable = true
    }

    schema.properties[field.name] = propertySchema
  })

  return schema
}

/**
 * Build a scoped schema based on keys of the value object.
 * The resulting schema will only contain properties and required fields that are present in the value object.
 *
 * @param schema Complete Ajv schema
 * @param value Value object to be validated
 * @returns Scoped Ajv schema
 */
export function buildScopedAjvSchema<T extends object>(schema: Record<string, any>, value: T): Record<string, any> {
  return {
    ...schema,
    properties: Object.keys(schema.properties)
      .filter((key: string) => key in value)
      .reduce(
        (acc, key) => {
          acc[key] = schema.properties[key]
          return acc
        },
        {} as Record<string, unknown>
      ),
    required: (schema.required as string[]).filter((key: string) => key in value),
  }
}

function getTranslatedAjvPropertySchema(property: Record<string, any>): Record<string, any> {
  const translatedSchema = JSON.parse(JSON.stringify(property))
  Object.keys(translatedSchema.errorMessage).forEach(key => {
    const translation = translated(translatedSchema.errorMessage[key] as keyof TranslatedTexts)
    if (translation) {
      translatedSchema.errorMessage[key] = translation
    }
  })
  return translatedSchema
}

/**
 * Return translated text of corresponding key. Undefined if not found.
 * @param key key of Translation
 * @returns translated text of corresponding key. Undefined if not found.
 */
function translated(key: keyof TranslatedTexts): string | undefined {
  try {
    return translations.texts[key].toString()
  } catch {
    return undefined
  }
}
